import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pointsToCentimeters, readPageSetup } from "@/office/word/layout";

export const lektionSeitenraender: Lesson = {
  id: "seitenraender",
  title: "Seitenränder und Ausrichtung",
  summary: "Wie viel Rand ein Dokument braucht – und wann Querformat sinnvoll ist.",
  durationMinutes: 8,
  learningGoals: [
    "Seitenränder bewusst einstellen",
    "Den Bundsteg beim beidseitigen Druck berücksichtigen",
    "Hoch- und Querformat gezielt einsetzen",
  ],
  steps: [
    {
      id: "raender",
      title: "Die Seitenränder",
      explanation: [
        "Der Seitenrand ist der weiße Bereich zwischen Papierkante und Text. Word " +
          "verwendet standardmäßig 2,5 cm ringsum – ein Wert, der für die meisten " +
          "Dokumente passt.",
        "Ränder sind kein verschenkter Platz. Sie geben dem Auge eine Ruhezone, machen " +
          "Platz für Notizen und verhindern, dass Text im Druck oder in der Bindung " +
          "verschwindet. Zu schmale Ränder wirken gedrängt und lassen Zeilen so lang " +
          "werden, dass man beim Zeilenwechsel die Spur verliert.",
        "Wird beidseitig gedruckt und gebunden, kommt der Bundsteg dazu: zusätzlicher " +
          "Platz an der Innenkante, damit in der Bindung nichts verschwindet. " +
          "Hochschulen verlangen dafür oft einen linken Rand von 3 cm oder mehr.",
      ],
      keyPoints: [
        "Layout → Seitenränder, mit Vorgaben und der Option „Benutzerdefiniert“.",
        "Standard sind 2,5 cm – für gebundene Arbeiten links mehr einplanen.",
        "Zu schmale Ränder machen die Zeilen zu lang zum bequemen Lesen.",
      ],
      task: {
        instruction:
          "Stelle den linken Seitenrand deines Dokuments auf mindestens 3 cm ein, wie es " +
          "für eine gebundene Arbeit üblich wäre.",
        hint: "Layout → Seitenränder → „Benutzerdefinierte Seitenränder…“ → Wert bei „Links“ eintragen.",
        check: async () => {
          const setup = await readPageSetup();
          if (setup === null) {
            return unsupported(
              "Die Seiteneinrichtung ist nur in Word für Windows und Mac auslesbar – in Word " +
                "im Web fehlt die Schnittstelle. Deshalb lässt sich diese Aufgabe hier nicht prüfen.",
              "Kontrolliere den Wert selbst unter Layout → Seitenränder und markiere den Schritt dann als erledigt.",
            );
          }

          const links = setup.leftMargin;
          const inCm = typeof links === "number" ? pointsToCentimeters(links) : null;

          return fromCriteria(
            [
              criterion(
                `Der linke Seitenrand beträgt mindestens 3 cm (gemessen: ${
                  inCm !== null ? `${inCm.toFixed(2).replace(".", ",")} cm` : "unbekannt"
                })`,
                inCm !== null && inCm >= 2.95,
              ),
            ],
            {
              success: "Passt – für die Bindung ist jetzt genug Platz.",
              failure: "Der linke Rand ist noch zu schmal.",
              hint: "Word rechnet intern in Punkt. Gib im Dialog einfach „3 cm“ ein, die Umrechnung übernimmt Word.",
            },
          );
        },
      },
    },
    {
      id: "ausrichtung",
      title: "Hoch- und Querformat",
      explanation: [
        "Hochformat ist der Normalfall. Querformat lohnt sich, wenn der Inhalt breiter als " +
          "hoch ist: breite Tabellen, Zeitpläne, Diagramme oder Handouts.",
        "Der häufigste Anwendungsfall in der Praxis: In einem Bericht im Hochformat soll " +
          "eine einzelne Seite mit einer breiten Tabelle quer stehen. Genau dafür brauchst " +
          "du die Abschnittsumbrüche aus der ersten Lektion – ohne sie dreht Word das " +
          "gesamte Dokument.",
        "Der Ablauf: vor und nach der betreffenden Seite je einen Abschnittsumbruch " +
          "setzen, den Cursor in den mittleren Abschnitt stellen und erst dann unter " +
          "„Layout“ → „Ausrichtung“ auf Querformat wechseln.",
      ],
      keyPoints: [
        "Layout → Ausrichtung → Hoch- oder Querformat.",
        "Die Ausrichtung gilt immer für den ganzen Abschnitt.",
        "Für eine einzelne Querseite: Abschnittsumbruch davor und danach.",
      ],
      task: {
        instruction:
          "Sieh dir die Ausrichtung deines Dokuments an und stelle sicher, dass es im " +
          "Hochformat steht.",
        hint: "Layout → Ausrichtung → Hochformat.",
        check: async () => {
          const setup = await readPageSetup();
          if (setup === null) {
            return unsupported(
              "Die Seiteneinrichtung ist nur in Word für Windows und Mac auslesbar – in Word " +
                "im Web fehlt die Schnittstelle.",
              "Prüfe die Ausrichtung selbst unter Layout → Ausrichtung und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Das Dokument steht im Hochformat (erkannt: ${setup.orientation ?? "unbekannt"})`,
                setup.orientation === "Portrait",
              ),
            ],
            {
              success: "Hochformat – die richtige Grundeinstellung für einen Bericht.",
              failure: "Das Dokument steht aktuell nicht im Hochformat.",
              hint: "Achte darauf, in welchem Abschnitt der Cursor steht – die Ausrichtung gilt immer nur für diesen.",
            },
          );
        },
      },
    },
  ],
};
